import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Lock, Trash2, X } from "lucide-react";
import { COMMON_LANGUAGES, languageName } from "@dishfolio/contracts";
import { Badge, Button, Card, CardDescription, CardHeader, CardTitle, ConfirmDialog, Field, Input, NativeSelect, Switch, toast } from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { useMenu } from "../lib/queries.ts";
import { PageContainer } from "../components/Page.tsx";
import { notifyError } from "../components/Upgrade.tsx";

export function MenuSettingsPage() {
  const { menuId } = useParams({ from: "/app/menus/$menuId/settings" });
  const menu = useMenu(menuId);
  const { org, can } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const m = menu.data?.menu;
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [langs, setLangs] = useState<string[]>([]);
  const [addLang, setAddLang] = useState("");
  const [del, setDel] = useState(false);
  useEffect(() => {
    if (m) {
      setName(m.name);
      setSlug(m.slug);
      setLangs(m.enabledLanguages);
    }
  }, [m]);
  if (!m || !org) return null;
  const maxExtra = org.plan.maxAdditionalLanguages;
  const extra = langs.filter((l) => l !== m.defaultLanguage).length;
  const save = async (patch: Record<string, unknown>) => {
    try {
      await api.patch(`/menus/${menuId}`, patch);
      await qc.invalidateQueries({ queryKey: ["menu", menuId] });
      await qc.invalidateQueries({ queryKey: ["menus"] });
      toast.success("Saved");
    } catch (err) {
      notifyError(err);
    }
  };
  return (
    <PageContainer>
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>General</CardTitle>
          </CardHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Menu name">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Public address" hint={m.publicUrl.replace(new RegExp(`/m/${m.slug}$`), `/m/${slug}`)}>
              <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} />
            </Field>
          </div>
          {slug !== m.slug ? <p className="text-xs text-destructive">Changing the address breaks QR codes you already printed.</p> : null}
          <Button className="justify-self-start" disabled={!can("menu:edit")} onClick={() => save({ name, ...(slug !== m.slug ? { slug } : {}) })}>
            Save
          </Button>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Languages</CardTitle>
            <CardDescription>
              Original language: {languageName(m.defaultLanguage)}. {maxExtra < 30 ? `Your plan includes ${maxExtra} extra language${maxExtra === 1 ? "" : "s"}.` : "Add as many as you need."}
            </CardDescription>
          </CardHeader>
          <div className="flex flex-wrap gap-2">
            {langs.map((l) => (
              <Badge key={l} variant={l === m.defaultLanguage ? "default" : "secondary"} className="gap-1.5 px-2.5 py-1 text-sm">
                {languageName(l)}
                {l !== m.defaultLanguage ? (
                  <button type="button" onClick={() => setLangs(langs.filter((x) => x !== l))} aria-label={`Remove ${l}`}>
                    <X className="size-3" />
                  </button>
                ) : null}
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <NativeSelect value={addLang} onChange={(e) => setAddLang(e.target.value)} className="max-w-xs" disabled={extra >= maxExtra}>
              <option value="">Add a language…</option>
              {COMMON_LANGUAGES.filter((l) => !langs.includes(l.tag)).map((l) => (
                <option key={l.tag} value={l.tag}>
                  {l.name} ({l.nativeName})
                </option>
              ))}
            </NativeSelect>
            <Button variant="outline" disabled={!addLang || extra >= maxExtra} onClick={() => (setLangs([...langs, addLang]), setAddLang(""))}>
              Add
            </Button>
          </div>
          {extra >= maxExtra && maxExtra < 30 ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="size-3" /> Upgrade to Pro for unlimited languages and AI translation.
            </p>
          ) : null}
          <Button className="justify-self-start" disabled={!can("menu:edit")} onClick={() => save({ enabledLanguages: langs })}>
            Save languages
          </Button>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Table ordering</CardTitle>
            <CardDescription>Let guests order from this menu at their table. Requires ordering to be switched on for the restaurant too.</CardDescription>
          </CardHeader>
          <label className="flex items-center justify-between gap-4 text-sm">
            <span>Accept orders from this menu</span>
            <Switch checked={m.orderingEnabled} disabled={!org.plan.tableOrdering || !can("menu:edit")} onCheckedChange={(v) => save({ orderingEnabled: v })} />
          </label>
          {!org.plan.tableOrdering ? <p className="text-xs text-muted-foreground">Available on the Business plan.</p> : null}
        </Card>
        {can("menu:publish") ? (
          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle>Delete menu</CardTitle>
              <CardDescription>Removes the menu and frees its address. Order history is kept.</CardDescription>
            </CardHeader>
            <Button variant="destructive" className="justify-self-start" onClick={() => setDel(true)}>
              <Trash2 /> Delete menu
            </Button>
          </Card>
        ) : null}
      </div>
      <ConfirmDialog
        open={del}
        onOpenChange={setDel}
        title={`Delete “${m.name}”?`}
        description="Guests will no longer be able to open it. This can’t be undone."
        destructive
        confirmLabel="Delete"
        onConfirm={async () => {
          await api.del(`/menus/${menuId}`);
          await qc.invalidateQueries({ queryKey: ["menus"] });
          await navigate({ to: "/menus" });
        }}
      />
    </PageContainer>
  );
}
